import React, { useMemo } from 'react';
import { GraphMember } from '@/contexts/FamilyGraphContext';
import { Award, Trophy, Sparkles, Users, GitBranch, Heart, Lock } from 'lucide-react';

interface Props {
  members: GraphMember[];
  memoriesCount: number;
  invitesSent?: number;
}

interface Badge {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  unlocked: boolean;
  tone: string; // tailwind gradient
}

/**
 * Lightweight achievement strip — purely visual, computed from existing
 * counts in the family graph. No DB writes, no extra schema.
 *
 * Achievements (low clutter, max 6):
 *   - First Relative Added      (1+ approved relative)
 *   - Family Starter            (3+ approved relatives)
 *   - 3 Generations Connected   (parent + grandparent or great-grandparent)
 *   - Family Builder            (10+ approved relatives)
 *   - Memory Keeper             (3+ memories saved)
 *   - Branch Connector          (1+ invite sent — tracked via the invites
 *                                table; passed in by the caller).
 */
const FamilyAchievements: React.FC<Props> = ({ members, memoriesCount, invitesSent = 0 }) => {
  const badges: Badge[] = useMemo(() => {
    const approved = members.filter((m) => m.approvalStatus === 'approved');
    const hasParent = approved.some(
      (m) =>
        m.relationship === 'mother' ||
        m.relationship === 'father' ||
        m.relationship === 'step-parent' ||
        m.relationship === 'adopted-parent'
    );
    const hasGrandOrGreat = approved.some(
      (m) => m.relationship === 'grandparent' || m.relationship === 'great-grandparent'
    );
    const threeGen = hasParent && hasGrandOrGreat;
    const memoriesWithNotes = memoriesCount + approved.filter((m) => !!m.notes).length;

    return [
      {
        id: 'first',
        label: 'First Relative',
        description: 'You added your first family member.',
        icon: <Sparkles className="w-4 h-4" />,
        unlocked: approved.length >= 1,
        tone: 'from-rose-500 to-pink-500',
      },
      {
        id: 'starter',
        label: 'Family Starter',
        description: '3 relatives confirmed in your tree.',
        icon: <Users className="w-4 h-4" />,
        unlocked: approved.length >= 3,
        tone: 'from-fuchsia-500 to-purple-500',
      },
      {
        id: 'three-gen',
        label: '3 Generations',
        description: 'Parents and grandparents are connected.',
        icon: <GitBranch className="w-4 h-4" />,
        unlocked: threeGen,
        tone: 'from-indigo-500 to-blue-500',
      },
      {
        id: 'builder',
        label: 'Family Builder',
        description: '10+ relatives in your circle.',
        icon: <Trophy className="w-4 h-4" />,
        unlocked: approved.length >= 10,
        tone: 'from-amber-500 to-orange-500',
      },
      {
        id: 'memory',
        label: 'Memory Keeper',
        description: 'Saved 3+ memories or biographies.',
        icon: <Heart className="w-4 h-4" />,
        unlocked: memoriesWithNotes >= 3,
        tone: 'from-emerald-500 to-teal-500',
      },
      {
        id: 'connector',
        label: 'Branch Connector',
        description: 'Sent an invite to grow the circle.',
        icon: <Award className="w-4 h-4" />,
        unlocked: invitesSent >= 1,
        tone: 'from-sky-500 to-cyan-500',
      },
    ];
  }, [members, memoriesCount, invitesSent]);

  const unlockedCount = badges.filter((b) => b.unlocked).length;

  return (
    <div className="rounded-2xl bg-white border-2 border-slate-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-5 h-5 text-amber-500" />
        <h3 className="font-serif font-bold text-lg text-slate-900">Achievements</h3>
        <span className="ml-auto text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
          {unlockedCount}/{badges.length}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {badges.map((b) => (
          <div
            key={b.id}
            className={`relative rounded-xl p-3 border transition ${
              b.unlocked
                ? 'bg-white border-slate-200 shadow-sm'
                : 'bg-slate-50 border-slate-100 opacity-70'
            }`}
            title={b.description}
          >
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white shadow-sm ${
                  b.unlocked
                    ? `bg-gradient-to-br ${b.tone}`
                    : 'bg-slate-300'
                }`}
              >
                {b.unlocked ? b.icon : <Lock className="w-3.5 h-3.5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={`text-xs font-bold leading-tight truncate ${
                    b.unlocked ? 'text-slate-900' : 'text-slate-500'
                  }`}
                >
                  {b.label}
                </div>
                <div className="text-[10px] text-slate-500 leading-tight line-clamp-2">
                  {b.description}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FamilyAchievements;
